/**
 * useDraggable — dependency-free drag-to-move for modals/dialogs.
 *
 * Returns a `transform` string to apply to the moving panel and an
 * `onHandleMouseDown` handler to attach to the drag handle (typically the
 * modal header / title bar). Dragging is ignored when the pointer starts on an
 * interactive control (button/link/input/select/textarea) so header buttons
 * still work. State resets on remount (modals unmount when closed).
 *
 * Usage (Tailwind modals): attach `onHandleMouseDown` to the header div and
 * apply `{ transform }` to the panel's style — see AppModal.
 */

import React, { useCallback, useRef, useState } from 'react';

const INTERACTIVE = 'button, a, input, select, textarea, [role="button"], [role="switch"]';

export function useDraggable(opts?: { handleSelector?: string }) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);

  const onHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (opts?.handleSelector && !target.closest(opts.handleSelector)) return;
      if (target.closest(INTERACTIVE)) return;
      e.preventDefault();
      drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };

      const move = (ev: MouseEvent) => {
        if (!drag.current) return;
        setPos({
          x: drag.current.ox + (ev.clientX - drag.current.sx),
          y: drag.current.oy + (ev.clientY - drag.current.sy),
        });
      };
      const up = () => {
        drag.current = null;
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
    },
    [pos.x, pos.y, opts?.handleSelector],
  );

  return {
    transform: `translate(${pos.x}px, ${pos.y}px)`,
    onHandleMouseDown,
    reset: useCallback(() => setPos({ x: 0, y: 0 }), []),
  };
}
